import { AVATARS, type PlayerProfile } from '@okwhere/shared';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { cn } from '../lib/cn';
import { Avatar } from './Avatar';

export function ProfileEditor({
  value,
  onChange,
  onSubmit,
  autoFocus = false,
}: {
  value: PlayerProfile;
  onChange: (profile: PlayerProfile) => void;
  onSubmit?: () => void;
  autoFocus?: boolean;
}) {
  const [picking, setPicking] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setPicking((open) => !open)}
          title="Change avatar"
          className="relative rounded-full transition hover:scale-105"
        >
          <Avatar emoji={value.avatar} size="lg" color="var(--color-primary-500)" />
          <span className="absolute -right-1 -bottom-1 grid size-6 place-items-center rounded-full bg-paper text-xs text-ink-950 shadow">
            ✎
          </span>
        </button>
        <form
          className="flex-1"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit?.();
          }}
        >
          <label className="text-xs font-bold tracking-[0.18em] text-ink-400 uppercase" htmlFor="nickname">
            Nickname
          </label>
          <input
            id="nickname"
            value={value.name}
            maxLength={20}
            autoFocus={autoFocus}
            autoComplete="nickname"
            placeholder="e.g. Captain Compass"
            onChange={(event) => onChange({ ...value, name: event.target.value })}
            className="mt-1 h-12 w-full rounded-2xl bg-ink-850 px-4 font-display text-lg font-bold ring-1 ring-white/10 transition outline-none placeholder:font-sans placeholder:text-base placeholder:font-normal placeholder:text-ink-500 focus:ring-2 focus:ring-primary-500"
          />
        </form>
      </div>
      <AnimatePresence initial={false}>
        {picking && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="grid max-h-64 grid-cols-6 gap-1.5 overflow-y-auto overscroll-contain rounded-2xl bg-ink-850 p-2 ring-1 ring-white/6 sm:grid-cols-8">
              {AVATARS.map((avatar) => (
                <button
                  key={avatar}
                  onClick={() => {
                    onChange({ ...value, avatar });
                    setPicking(false);
                  }}
                  className={cn(
                    'grid aspect-square place-items-center rounded-xl text-2xl transition hover:scale-110 hover:bg-ink-700',
                    avatar === value.avatar && 'bg-ink-700 ring-2 ring-primary-500',
                  )}
                >
                  {avatar}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
